import React, { useState } from 'react';
import {
  X,
  MessageSquarePlus,
  Send,
  // SendHorizonal, // TODO(ROG-1942): Resend hidden — uncomment when restoring
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { Button } from '@roger/ui-components';
import {
  checkoutOrder,
  checkinOrder,
  // resendOrder, // TODO(ROG-1942): Resend hidden — uncomment when restoring
  client,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import CommentDialog from './CommentDialog';

type OrderManagerTabId = 'to-be-sent' | 'pending-signature' | 'received';

interface OrderManagerActionsProps {
  activeTab: OrderManagerTabId;
  selectedIds: string[];
  onClearSelection: () => void;
  onActionComplete: () => void;
}

const OrderManagerActions: React.FC<OrderManagerActionsProps> = ({
  activeTab,
  selectedIds,
  onClearSelection,
  onActionComplete,
}) => {
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { error: showError } = useToast();
  const count = selectedIds.length;

  if (count === 0) return null;

  const executeBulkAction = async (
    action: (params: {
      path: { order_id: string };
      client: typeof client;
    }) => Promise<unknown>,
    verb: string
  ) => {
    setIsLoading(true);
    const results = await Promise.allSettled(
      selectedIds.map(id => action({ path: { order_id: id }, client }))
    );
    const failedCount = results.filter(r => r.status === 'rejected').length;
    if (failedCount > 0) {
      showError(`Failed to ${verb} ${failedCount} of ${count} orders`);
    }
    setIsLoading(false);
    onClearSelection();
    onActionComplete();
  };

  return (
    <>
      <div className='fixed bottom-6 left-1/2 z-50 -translate-x-1/2 transform'>
        <div className='flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg'>
          <span className='text-sm font-medium'>{count} selected</span>
          <Button
            variant='ghost'
            size='sm'
            onClick={onClearSelection}
            disabled={isLoading}
          >
            <X className='h-4 w-4' />
          </Button>

          <div className='mx-1 h-6 w-px bg-border' />

          <Button
            variant='outline'
            size='sm'
            onClick={() => setCommentDialogOpen(true)}
            disabled={isLoading}
          >
            <MessageSquarePlus className='mr-1.5 h-4 w-4' />
            Comment
          </Button>

          {activeTab === 'to-be-sent' && (
            <Button
              size='sm'
              onClick={() => executeBulkAction(checkoutOrder, 'checkout')}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
              ) : (
                <Send className='mr-1.5 h-4 w-4' />
              )}
              Checkout
            </Button>
          )}

          {activeTab === 'pending-signature' && (
            <>
              {/* TODO(ROG-1942): Resend button hidden per product decision (2026-04).
                  Revisit to delete or promote to a feature flag within 2 sprints.
              <Button
                variant='outline'
                size='sm'
                onClick={() => executeBulkAction(resendOrder, 'resend')}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                ) : (
                  <SendHorizonal className='mr-1.5 h-4 w-4' />
                )}
                Resend
              </Button>
              */}
              <Button
                size='sm'
                onClick={() => executeBulkAction(checkinOrder, 'check in')}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                ) : (
                  <CheckCircle2 className='mr-1.5 h-4 w-4' />
                )}
                Check In
              </Button>
            </>
          )}
        </div>
      </div>

      <CommentDialog
        open={commentDialogOpen}
        onOpenChange={setCommentDialogOpen}
        orderIds={selectedIds}
        mode='bulk'
        onSuccess={() => {
          onClearSelection();
          onActionComplete();
        }}
      />
    </>
  );
};

export default OrderManagerActions;
